import type { Control } from 'react-hook-form'
import { useController, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Input, Text } from '@enechain/ecloud-designsystem'

import {
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import {
  PLAN_AUTO_SUBMIT_OFFSET_MAX_MINUTES,
  isPlanAutoSubmitOffsetFieldShown,
  resolvePlanAutoSubmitOffset,
} from '~/pages/AdminWorkflowTemplates/models/planAutoSubmitOffset'
import type { WorkflowTemplateFormValues } from '~/pages/AdminWorkflowTemplates/models/schema'

type Props = {
  automationAvailable: boolean
  bigStepIndex: number
  control: Control<WorkflowTemplateFormValues>
  subIndex: number
}

/**
 * 提出オフセット（分）欄 (spec AC-WFT-041 / AC-WFT-042)。 表示条件は resolver
 * (`models/planAutoSubmitOffset.ts`) が決め、flag はここで表示にだけ使う
 * (編集済みの行は flag に関わらず出す)。
 * 打った文字列は `planAutoSubmitOffsetInput` に生のまま書き、検証は schema が行う。
 */
export const SubStepPlanAutoSubmitOffsetField = ({
  automationAvailable,
  bigStepIndex,
  control,
  subIndex,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const path = `bigSteps.${bigStepIndex}.subSteps.${subIndex}` as const
  const row = useWatch({ control, name: path })
  const input = useController({
    control,
    name: `${path}.planAutoSubmitOffsetInput`,
  })

  const resolution = resolvePlanAutoSubmitOffset(row)
  if (!isPlanAutoSubmitOffsetFieldShown(resolution, automationAvailable))
    return null

  const inputId = `plan-auto-submit-offset-${bigStepIndex}-${subIndex}`
  const label = t('page:adminWorkflowTemplates.subStep.planAutoSubmitOffset')
  return (
    <Box paddingLeft="32px">
      <FieldRoot
        width="full"
        maxWidth="560px"
        invalid={input.fieldState.invalid}
        required
      >
        <FieldLabel htmlFor={inputId}>
          {label}
          <Text as="span" color="Text.negativeHigh">
            *
          </Text>
        </FieldLabel>
        <Input
          ref={input.field.ref}
          width="120px"
          aria-describedby={
            input.fieldState.invalid ? `${inputId}-error` : undefined
          }
          aria-label={label}
          id={inputId}
          inputMode="numeric"
          max={PLAN_AUTO_SUBMIT_OFFSET_MAX_MINUTES}
          min={0}
          name={input.field.name}
          onBlur={input.field.onBlur}
          onChange={(e) => {
            input.field.onChange(e.target.value)
          }}
          step={1}
          type="number"
          value={resolution.kind === 'notHolder' ? '' : resolution.text}
        />
        <FieldErrorText id={`${inputId}-error`}>
          {input.fieldState.error?.message}
        </FieldErrorText>
        <FieldHelperText color="Text.midEmphasis">
          {t('page:adminWorkflowTemplates.subStep.planAutoSubmitOffsetHint')}
        </FieldHelperText>
      </FieldRoot>
    </Box>
  )
}
